import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Receipt, Printer, TestTube, Sparkles, MessageSquare, TrendingDown, TrendingUp, FileText } from "lucide-react";

const ASSESSMENT_LABEL = {
  monthly: "Monthly", special: "Special", late_fee: "Late fee", credit: "Payment", other: "Other",
};

export default function MyBalance() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [selectedSpecial, setSelectedSpecial] = useState(null);
  const [inqOpen, setInqOpen] = useState(false);
  const [inqForm, setInqForm] = useState({ subject: "", body: "", category: "balance" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      const { data } = await api.get(`/my-ledger?${params.toString()}`);
      setData(data);
    } catch (err) {
      console.error("ledger load failed:", err);
      toast.error(err.response?.data?.detail || "Could not load ledger");
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const openPrint = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const params = new URLSearchParams();
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    // The endpoint reads Authorization from the SPA, so include the token via a header — since we can't
    // set headers on window.open, we use a signed download link approach: the endpoint will read the
    // session cookie or we pass the token as a query param. For simplest demo behavior we open the
    // page and it will render since the cookie is set on login.
    const token = localStorage.getItem("commoniq_token") || "";
    // Include token as query param since window.open can't set headers
    params.set("t", token);
    window.open(`${base}/api/my-ledger/statement.html?${params.toString()}`, "_blank", "noopener");
  };

  const submitInquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/inquiries", inqForm);
      toast.success("Inquiry submitted — board will respond within 5 days");
      setInqOpen(false);
      setInqForm({ subject: "", body: "", category: "balance" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit");
    } finally { setSending(false); }
  };

  if (!data) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="my-balance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-secondary text-sm font-medium mb-2">
            <Receipt className="w-4 h-4" /> Your account
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">Balance & statement</h1>
          <p className="text-foreground/70 mt-1">Every charge, payment, and running balance — with a printable statement for your records.</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={inqOpen} onOpenChange={setInqOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-full" data-testid="submit-inquiry-btn">
                <MessageSquare className="w-4 h-4 mr-1.5" /> Ask the Board
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Submit inquiry to the Board</DialogTitle></DialogHeader>
              <form onSubmit={submitInquiry} className="space-y-3" data-testid="inquiry-form">
                <div>
                  <Label>Subject</Label>
                  <Input required value={inqForm.subject} onChange={(e) => setInqForm({ ...inqForm, subject: e.target.value })} className="mt-1.5 h-11" data-testid="inq-subject-input" />
                </div>
                <div>
                  <Label>Details</Label>
                  <Textarea required minLength={20} value={inqForm.body} onChange={(e) => setInqForm({ ...inqForm, body: e.target.value })} className="mt-1.5 min-h-[110px]" placeholder="Please explain your question…" data-testid="inq-body-input" />
                </div>
                <div className="text-xs text-muted-foreground bg-primary/10 p-2 rounded-lg">
                  🕒 Board response SLA: 5 business days. You'll receive updates in your dashboard.
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={sending} className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" data-testid="inq-submit-btn">
                    {sending ? "Submitting…" : "Submit"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          <Button className="rounded-full bg-gold text-secondary-foreground hover:brightness-110" onClick={openPrint} data-testid="print-statement-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print statement
          </Button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <TotalTile label="Monthly assessments" value={t.monthly_charged} icon={TrendingDown} tone="red" />
        <TotalTile label="Special assessments" value={t.special_charged} icon={TrendingDown} tone="red" />
        <TotalTile label="Late fees" value={t.fees_charged} icon={TrendingDown} tone="red" />
        <TotalTile label="Payments received" value={t.payments} icon={TrendingUp} tone="green" prefix="−" />
        <TotalTile label="Current balance" value={t.balance} icon={Receipt} tone={t.balance > 0 ? "red" : "green"} highlight />
      </div>

      {/* Date range */}
      <div className="hoa-card p-4 flex flex-wrap items-end gap-3" data-testid="date-range-filter">
        <div>
          <Label>Start</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5 h-11" data-testid="range-start-input" />
        </div>
        <div>
          <Label>End</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5 h-11" data-testid="range-end-input" />
        </div>
        <Button variant="ghost" className="rounded-full" onClick={() => { setStart(""); setEnd(""); }} data-testid="range-clear-btn">Clear</Button>
        <div className="text-xs text-muted-foreground ml-auto self-center">Statement PDF respects the date range above.</div>
      </div>

      {/* Ledger table */}
      <div className="hoa-card overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-4">Description</div>
          <div className="col-span-2 text-right">Amount</div>
          <div className="col-span-2 text-right">Running</div>
        </div>
        {data.entries.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No ledger entries. Load sample data from your board dashboard to try the flow.</div>
        ) : data.entries.map((e) => {
          const isCharge = e.kind === "charge";
          const isSpecial = e.assessment_type === "special";
          return (
            <button
              type="button"
              key={e.id}
              disabled={!isSpecial}
              onClick={() => isSpecial && setSelectedSpecial(e)}
              className={`grid grid-cols-12 gap-3 px-4 py-3 border-b border-border/50 items-center w-full text-left ${isSpecial ? "hover:bg-accent/40 cursor-pointer" : "cursor-default"}`}
              data-testid={`ledger-row-${e.id}`}
            >
              <div className="col-span-2 text-xs text-muted-foreground">{new Date(e.created_at).toLocaleDateString()}</div>
              <div className="col-span-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${isCharge ? (isSpecial ? "bg-secondary/20 text-secondary" : "bg-destructive/15 text-destructive") : "bg-[hsl(140_55%_45%/0.18)] text-[hsl(140_65%_45%)]"}`}>
                  {ASSESSMENT_LABEL[e.assessment_type] || e.assessment_type}
                </span>
                {isSpecial && <span className="ml-1 text-[10px] text-secondary underline">details</span>}
              </div>
              <div className="col-span-4 text-sm">{e.description}</div>
              <div className={`col-span-2 text-right font-mono font-semibold ${isCharge ? "text-destructive" : "text-[hsl(140_65%_45%)]"}`}>
                {isCharge ? "+" : "−"}${e.amount.toFixed(2)}
              </div>
              <div className="col-span-2 text-right font-mono text-foreground/85">${(e.running_balance || 0).toFixed(2)}</div>
            </button>
          );
        })}
      </div>

      {/* Special assessment detail modal */}
      <Dialog open={!!selectedSpecial} onOpenChange={(o) => !o && setSelectedSpecial(null)}>
        <DialogContent data-testid="special-detail-modal">
          <DialogHeader><DialogTitle>Special assessment · details</DialogTitle></DialogHeader>
          {selectedSpecial && (
            <div className="space-y-3 text-sm">
              <div className="p-3 rounded-lg bg-accent/30 border border-border">
                <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-1">Amount charged</div>
                <div className="text-2xl font-mono text-destructive font-semibold">${selectedSpecial.amount.toFixed(2)}</div>
                <div className="text-xs text-muted-foreground mt-1">{selectedSpecial.description}</div>
              </div>
              {selectedSpecial.special_assessment_reason && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-secondary font-semibold mb-1">Reason</div>
                  <p className="text-foreground/85">{selectedSpecial.special_assessment_reason}</p>
                </div>
              )}
              {selectedSpecial.vote_citation && (
                <div className="p-3 rounded-lg bg-gold/10 border border-gold/30 flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-gold font-semibold mb-1">Governing-doc citation</div>
                    <p className="text-foreground/85 text-sm">{selectedSpecial.vote_citation}</p>
                  </div>
                </div>
              )}
              <Link to="/documents" className="text-sm text-gold hover:underline inline-flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> View meeting minutes & governing docs
              </Link>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TotalTile({ label, value, icon: Icon, tone, highlight, prefix = "" }) {
  const tones = {
    red: "text-destructive", green: "text-[hsl(140_65%_45%)]",
  };
  return (
    <div className={`hoa-card p-4 ${highlight ? "royal-ring" : ""}`} data-testid={`total-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-xl font-mono font-semibold ${tones[tone] || "text-foreground"}`}>
        {prefix}${Math.abs(value).toFixed(2)}
      </div>
    </div>
  );
}
